import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { TrendingUp, Upload, FileSpreadsheet, Plus, ArrowRight, AlertCircle, Archive, ArchiveRestore, Trash2, Eye, EyeOff } from "lucide-react";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

export default function ProformaList() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isAdmin = user?.role === "admin";
  const [template, setTemplate] = useState(null);
  const [instances, setInstances] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [creating, setCreating] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    try {
      const [tRes, iRes] = await Promise.all([
        api.get("/proforma/template").catch(() => ({ data: null })),
        api.get("/proformas", { params: { include_archived: showArchived } }),
      ]);
      setTemplate(tRes?.data || null);
      setInstances(iRes.data || []);
    } catch (e) { console.error(e); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [showArchived]);

  const archiveProforma = async (e, p) => {
    e.preventDefault(); e.stopPropagation();
    try {
      await api.post(`/proformas/${p.id}/archive`);
      toast.success(`Archived "${p.title}"`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Archive failed"); }
  };
  const unarchiveProforma = async (e, p) => {
    e.preventDefault(); e.stopPropagation();
    try {
      await api.post(`/proformas/${p.id}/unarchive`);
      toast.success(`Restored "${p.title}"`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Restore failed"); }
  };
  const deleteProforma = async (e, p) => {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.\n\nIf you might need it later, use Archive instead.`)) return;
    try {
      await api.delete(`/proformas/${p.id}`);
      toast.success(`Deleted "${p.title}"`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  // ------- Bulk selection + bulk actions -------
  const toggleRow = (e, id) => {
    e.preventDefault(); e.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const selectAll = () => setSelectedIds(new Set(instances.map((p) => p.id)));
  const runBulk = async (action) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (action === "delete" && !window.confirm(`Delete ${ids.length} proforma(s)? This cannot be undone.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post(`/proformas/bulk-${action}`, { ids });
      const skipped = data.skipped?.length || 0;
      toast.success(`${action === "archive" ? "Archived" : action === "unarchive" ? "Restored" : "Deleted"} ${data.ok} proforma(s)${skipped ? ` · ${skipped} skipped` : ""}`);
      clearSelection();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk action failed"); }
    finally { setBulkBusy(false); }
  };

  const uploadTemplate = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/proforma/template", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Template uploaded — ${data.sheets} sheet(s)`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const createInstance = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) { toast.error("Give this proforma a name"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/proformas", { title: newTitle.trim() });
      toast.success("Proforma created");
      nav(`/proforma/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create proforma");
    } finally { setCreating(false); }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="proforma-list-page">
        <div className="mb-8">
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">Reinsurance Modeling</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-[#0B132B] mt-2 tracking-tight leading-none">
            Proforma
          </h1>
          <p className="text-sm text-[#6C757D] mt-3 max-w-2xl">
            Fill in agent-specific inputs (monthly production, average VSC price, retention rate) — the workbook recalculates and can be exported as PDF or emailed to the dealer.
          </p>
        </div>

        {/* Template state */}
        {template ? (
          <Card className="p-6 mb-8 border-[#E9ECEF] rounded-sm flex items-start gap-4" data-testid="proforma-template-card">
            <div className="w-12 h-12 rounded-sm bg-[#0B132B] text-white flex items-center justify-center shrink-0">
              <FileSpreadsheet size={22} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#6C757D]">Master template</div>
              <div className="text-base font-bold text-[#0B132B] truncate">{template.filename}</div>
              <div className="text-xs text-[#6C757D] mt-1">
                {(template.sheets?.length ?? 0)} sheet(s) · {template.total_inputs ?? 0} input cells · uploaded {fmtDate(template.uploaded_at)}
              </div>
            </div>
            {isAdmin && (
              <label className="shrink-0">
                <input type="file" accept=".xlsx" onChange={uploadTemplate} className="hidden" data-testid="proforma-template-replace" />
                <span className="inline-flex items-center gap-2 h-10 px-4 border border-[#0B132B]/20 rounded-sm cursor-pointer text-xs uppercase tracking-[0.16em] font-bold text-[#0B132B] hover:bg-[#0B132B] hover:text-white transition-colors">
                  <Upload size={14} /> {uploading ? "Uploading…" : "Replace"}
                </span>
              </label>
            )}
          </Card>
        ) : isAdmin ? (
          <Card className="p-6 mb-8 border-2 border-dashed border-[#D4AF37] rounded-sm text-center bg-[#D4AF37]/5" data-testid="proforma-template-upload">
            <FileSpreadsheet size={32} className="mx-auto text-[#D4AF37]" />
            <div className="mt-3 text-lg font-bold text-[#0B132B]">Upload your proforma template</div>
            <p className="text-sm text-[#6C757D] mt-1 max-w-lg mx-auto">
              Drop the master reinsurance workbook. Agents will then be able to spin up per-dealer proformas that only ask for the inputs you've left blank.
            </p>
            <label className="inline-block mt-4">
              <input type="file" accept=".xlsx" onChange={uploadTemplate} className="hidden" data-testid="proforma-template-input" />
              <span className="inline-flex items-center gap-2 h-11 px-6 bg-[#0B132B] text-white rounded-sm cursor-pointer text-sm font-semibold hover:bg-[#1C2541] transition-colors">
                <Upload size={16} /> {uploading ? "Uploading…" : "Choose .xlsx"}
              </span>
            </label>
          </Card>
        ) : (
          <Card className="p-6 mb-8 border-[#E9ECEF] rounded-sm text-center bg-white" data-testid="proforma-no-template">
            <AlertCircle size={28} className="mx-auto text-[#6C757D]" />
            <div className="mt-2 font-semibold text-[#0B132B]">No template uploaded yet</div>
            <p className="text-sm text-[#6C757D] mt-1">Ask a Super Admin to upload the master proforma workbook.</p>
          </Card>
        )}

        {/* Create new proforma */}
        {template && (
          <Card className="p-6 mb-8 border-[#E9ECEF] rounded-sm" data-testid="proforma-create-card">
            <form onSubmit={createInstance} className="flex flex-col sm:flex-row gap-3 sm:items-end">
              <div className="flex-1">
                <Label className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B]">Name your new proforma</Label>
                <Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="e.g. Cannon Honda — 2026 launch scenario"
                  className="mt-2 h-11 rounded-sm border-[#0B132B]/15" data-testid="proforma-new-title" />
              </div>
              <Button type="submit" disabled={creating}
                className="h-11 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white font-semibold px-6"
                data-testid="proforma-create-button">
                <Plus size={16} className="mr-2" /> {creating ? "Creating…" : "Create proforma"}
              </Button>
            </form>
          </Card>
        )}

        {/* Instances list */}
        <div className="space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#0B132B]">Your proformas</div>
            <div className="flex items-center gap-3">
              {instances.length > 0 && (
                <button type="button"
                  onClick={() => selectedIds.size === instances.length ? clearSelection() : selectAll()}
                  className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#6C757D] hover:text-[#0B132B]"
                  data-testid="proforma-select-all">
                  {selectedIds.size === instances.length ? "Deselect all" : "Select all"}
                </button>
              )}
              <button type="button" onClick={() => setShowArchived((v) => !v)}
                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] font-bold text-[#6C757D] hover:text-[#0B132B] transition-colors"
                data-testid="proforma-toggle-archived">
                {showArchived ? <><EyeOff size={12}/> Hide archived</> : <><Eye size={12}/> Show archived</>}
              </button>
            </div>
          </div>

          {/* Bulk action toolbar */}
          {selectedIds.size > 0 && (
            <Card className="p-3 border-[#D4AF37] bg-[#D4AF37]/10 rounded-sm flex flex-wrap items-center gap-3" data-testid="proforma-bulk-toolbar">
              <div className="text-sm font-bold text-[#0B132B] flex-1">
                {selectedIds.size} proforma(s) selected
              </div>
              <Button size="sm" variant="outline" onClick={() => runBulk("archive")} disabled={bulkBusy}
                className="h-8 rounded-sm border-[#0B132B]/20 text-[#0B132B] bg-white" data-testid="proforma-bulk-archive">
                <Archive size={12} className="mr-1"/> Archive
              </Button>
              {showArchived && (
                <Button size="sm" variant="outline" onClick={() => runBulk("unarchive")} disabled={bulkBusy}
                  className="h-8 rounded-sm border-[#0B132B]/20 text-[#0B132B] bg-white" data-testid="proforma-bulk-unarchive">
                  <ArchiveRestore size={12} className="mr-1"/> Restore
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => runBulk("delete")} disabled={bulkBusy}
                className="h-8 rounded-sm border-red-200 text-red-600 hover:bg-red-50 bg-white" data-testid="proforma-bulk-delete">
                <Trash2 size={12} className="mr-1"/> Delete
              </Button>
              <button type="button" onClick={clearSelection} className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#6C757D] hover:text-[#0B132B]" data-testid="proforma-bulk-clear">
                Clear
              </button>
            </Card>
          )}
          {instances.length === 0 ? (
            <Card className="p-10 border-dashed border-2 border-[#E9ECEF] rounded-sm text-center bg-white">
              <TrendingUp size={28} className="mx-auto text-[#6C757D]" />
              <div className="mt-2 text-sm text-[#6C757D]">No proformas yet. Create one above to get started.</div>
            </Card>
          ) : (
            instances.map((p) => (
              <div key={p.id} className="relative" data-testid={`proforma-row-${p.id}`}>
                <Link to={`/proforma/${p.id}`} className="block group">
                  <Card className={`p-5 pl-14 pr-40 border-[#E9ECEF] rounded-sm group-hover:border-[#D4AF37] transition-colors flex items-center gap-4 ${p.archived ? "opacity-70 bg-[#F8F9FA]" : ""} ${selectedIds.has(p.id) ? "ring-2 ring-[#D4AF37]" : ""}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-base font-bold text-[#0B132B] truncate">{p.title}</div>
                        {p.archived && (
                          <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.14em] font-bold text-[#6C757D] bg-white border border-[#E9ECEF] px-2 py-0.5 rounded-sm" data-testid={`proforma-archived-badge-${p.id}`}>
                            <Archive size={10}/> Archived
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-[#6C757D] mt-0.5">
                        Updated {fmtDate(p.updated_at)} · {Object.keys(p.inputs || {}).length} filled input(s)
                        {p.archived_at && ` · Archived ${fmtDate(p.archived_at)}`}
                      </div>
                    </div>
                    <ArrowRight size={16} className="text-[#6C757D] group-hover:text-[#D4AF37] transition-colors" />
                  </Card>
                </Link>
                {/* Row select checkbox (overlaid on left) */}
                <input
                  type="checkbox"
                  checked={selectedIds.has(p.id)}
                  onChange={(e) => toggleRow(e, p.id)}
                  onClick={(e) => e.stopPropagation()}
                  className="absolute top-1/2 left-4 -translate-y-1/2 cursor-pointer z-10"
                  data-testid={`proforma-select-${p.id}`}
                  aria-label={`Select ${p.title}`}
                />
                {/* Row actions overlaid on the right so the whole card still navigates */}
                <div className="absolute top-1/2 right-4 -translate-y-1/2 flex items-center gap-1.5">
                  {p.archived ? (
                    <Button size="sm" variant="outline" onClick={(e) => unarchiveProforma(e, p)}
                      className="h-8 rounded-sm border-[#0B132B]/20 text-[#0B132B] bg-white"
                      data-testid={`proforma-unarchive-${p.id}`} title="Restore">
                      <ArchiveRestore size={12} className="mr-1" /> Restore
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={(e) => archiveProforma(e, p)}
                      className="h-8 rounded-sm border-[#0B132B]/20 text-[#6C757D] hover:text-[#0B132B] bg-white"
                      data-testid={`proforma-archive-${p.id}`} title="Hide from the list">
                      <Archive size={12} className="mr-1" /> Archive
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={(e) => deleteProforma(e, p)}
                    className="h-8 rounded-sm border-red-200 text-red-600 hover:bg-red-50 bg-white"
                    data-testid={`proforma-delete-${p.id}`} title="Permanently delete">
                    <Trash2 size={12} />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </main>
    </div>
  );
}
